import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useGetTeacherSubjectsQuery, useGetTeacherOverviewStatsQuery } from "../../../../../app/questionApiSlice";
import { selectCurrentUser } from "../../../auth/authSlice";
import quizIcon from "../../../../../assets/icons/quiz-icon-overview.svg";
import notesIcon from "../../../../../assets/icons/notes-icon.svg";
import mediaIcon from "../../../../../assets/icons/media-icon.svg";
import reviewIcon from "../../../../../assets/icons/review-icon.svg";

const Overview = () => {
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);

  // Get user roles
  const userRoles = user?.roles?.map((item) => item?.role?.name).filter(Boolean) || [];
  const isTeacher = userRoles.includes("teacher");
  const isReviewer = userRoles.includes("reviewer");
  const isAdmin = userRoles.includes("admin");
  const isAdminOnly = isAdmin && !isTeacher;

  const { data: teacherSubjects } = useGetTeacherSubjectsQuery(undefined, { skip: isAdminOnly });
  const { data: stats } = useGetTeacherOverviewStatsQuery(undefined, { skip: isAdminOnly });
  const hasSubjects = teacherSubjects && teacherSubjects.length > 0;

  // Redirect admin-only users straight to questions
  useEffect(() => {
    if (isAdminOnly) {
      navigate("/lms/questions", { replace: true });
    }
  }, [isAdminOnly, navigate]);

  // Redirect if teacher has no subjects but is a reviewer
  useEffect(() => {
    if (!isAdminOnly && isTeacher && !hasSubjects && isReviewer) {
      navigate("/lms/questions");
    }
  }, [isAdminOnly, hasSubjects, isTeacher, isReviewer, navigate]);

  // If teacher has no subjects and no reviewer role, show message
  if (isTeacher && !hasSubjects && !isReviewer) {
    return (
      <Box sx={{ padding: "2em", textAlign: "center" }}>
        <Typography variant="h6" sx={{ color: "text.secondary" }}>
          You need to be assigned to at least one subject to access the teacher dashboard.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "auto",
            sm: "auto",
            md: "auto auto ",
            lg: "auto auto auto",
          },
          gap: { xs: 2.5, sm: 2, md: 2, lg: 2.5 },
        }}
      >
        {getOverviewCards(stats).map((card) => (
          <Box
            sx={{
              borderRadius: "6px",
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
              padding: "2em 1.5em",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              width: "100%",
              minWidth: "18.2em",
              cursor: "pointer",
              transition: "box-shadow 0.3s ease, transform 0.3s ease",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                transform: "translateY(-2px)",
              },
            }}
            key={card.id}
            onClick={() => navigate(card.link)}
          >
            <Box
              sx={{
                padding: ".5em .7em",
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F4F6FAB2",
                borderRadius: "10px",
                marginBottom: ".5em",
                width: "3em",
              }}
            >
              <img src={card.icon} alt="" style={{ width: "100%" }} />
            </Box>
            <Typography
              variant="body2"
              sx={{ fontSize: "1rem", fontWeight: 500 }}
            >
              {card.text} ({card.number})
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const getOverviewCards = (stats) => [
  {
    id: 3,
    icon: quizIcon,
    text: "Questions",
    number: stats?.total_questions || "0",
    link: "/lms/questions",
  },
  {
    id: 4,
    icon: quizIcon,
    text: "Quizzes",
    number: stats?.total_quizzes || "0",
    link: "/lms/quizzes",
  },
  {
    id: 5,
    icon: notesIcon,
    text: "Notes",
    number: stats?.total_notes ?? "0",
    link: "/lms/notes",
  },
  {
    id: 6,
    icon: reviewIcon,
    text: "Reviews",
    number: stats?.total_reviews_received || "0",
    link: "/lms/reviews",
  },
];

export default Overview;
